import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
  Divider,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from "@mui/icons-material";

// Import exact action creators from your action file
import {
  getSystemDeviceList,
  deleteSystemDevice,
} from "../../../../store/actions";
import AddEditPosSystemModal from "../../../../Components/popUpComponents/AddEditPosSystemModal/AddEditPosSystemModal";

// Adjust this import path to point to your actual DeleteModal location
import DeleteModal from "../../../../Components/Common/DeleteModal";

import SettingsPanel from "../../components/SettingsPanel";

const SystemDeviceSettings = () => {
  const dispatch = useDispatch();

  // 1. Selector mapping based exactly on your reducer.js structure
  const { systemDeviceList: devicesData, systemDeviceListLoading } =
    useSelector((state) => ({
      systemDeviceList: state.SystemDevice?.systemDeviceList || [],
      systemDeviceListLoading: state.SystemDevice?.systemDeviceListLoading,
    }));

  // Safely extract the array based on the new API response
  const devices = Array.isArray(devicesData)
    ? devicesData
    : devicesData?.possystem_list || [];

  // 2. Local state for controlling Modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);

  // States for Delete Modal
  const [deleteModal, setDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // 3. Fetch List on Mount
  useEffect(() => {
    dispatch(getSystemDeviceList());
  }, [dispatch]);

  // 4. Modal Handlers
  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const handleCreateClick = () => {
    setIsEdit(false);
    setSelectedDevice(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (device) => {
    setIsEdit(true);
    setSelectedDevice(device);
    setIsModalOpen(true);
  };

  // Opens the delete modal and stores the ID
  const handleDeleteClick = (id) => {
    setItemToDelete(id);
    setDeleteModal(true);
  };

  // Dispatches the delete action and closes the modal
  const handleDeleteConfirm = () => {
    if (itemToDelete) {
      dispatch(deleteSystemDevice([itemToDelete]));
      setDeleteModal(false);
      setItemToDelete(null);
    }
  };

  return (
    <SettingsPanel
      title="POS System Settings"
      description="POS terminals and devices registered to this company"
    >
      <Box sx={{ p: 3, maxWidth: 1200, margin: "0 auto" }}>
        {/* Header Area with Add Button */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateClick}
            sx={{ px: 3, py: 1 }}
          >
            Create New System
          </Button>
        </Box>

        {/* List Section */}
        <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Box sx={{ p: 3, bgcolor: "background.default" }}>
            <Typography variant="h6">POS System List</Typography>
          </Box>
          <Divider />

          {systemDeviceListLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table sx={{ minWidth: 650 }} aria-label="pos system table">
                <TableHead sx={{ bgcolor: "action.hover" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold" }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Store Name
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      External ID
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Created Date
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {devices && devices.length > 0 ? (
                    devices.map((device) => {
                      const { Id, StoreName, IdExternal, CreatedDateTime } =
                        device;
                      return (
                        <TableRow
                          key={Id}
                          hover
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell>{Id}</TableCell>
                          <TableCell component="th" scope="row">
                            {StoreName || (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                N/A
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {IdExternal || (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                N/A
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {CreatedDateTime || (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                N/A
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Edit System">
                              <IconButton
                                color="primary"
                                onClick={() => handleEditClick(device)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete System">
                              <IconButton
                                color="error"
                                onClick={() => handleDeleteClick(Id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                          No systems found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* Render the Create/Edit Modal Component */}
        <AddEditPosSystemModal
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
          toggle={toggleModal}
          isEdit={isEdit}
          editData={selectedDevice}
        />

        {/* Render the Delete Modal Component */}
        {deleteModal && (
          <DeleteModal
            show={deleteModal}
            onDeleteClick={handleDeleteConfirm}
            onCloseClick={() => {
              setDeleteModal(false);
              setItemToDelete(null);
            }}
          />
        )}
      </Box>
    </SettingsPanel>
  );
};

export default SystemDeviceSettings;
